import { Button } from "react-bootstrap";
import { Col, Form, Row, Select, Space } from "antd";
import { useMemo } from "react";
import moment from "moment";
import { useHistory, useLocation } from "react-router-dom";

import { withProcedureWraper } from "../..";
import { Box, DatePicker, Table, UserManual } from "../../../../components";
import { useMTPRequests } from "../monthly-training-plan/hooks";
import {
  DateFormat,
  DepartmentCode,
  RequestStatus,
} from "../../../../constants";
import { Routes } from "../../../../AppRoutes";
import { useMTPSMyRequests, useMTPSColumns } from "./hooks";
import { StringHelper } from "../../../../helper";
import { MTPDetailTable } from "./components";
import { transform } from "./utils/transform";
import { MTPExcelJSService } from "../monthly-training-plan/services/MTPExcelJS.service";

const depOptions = [
  {
    label: "Khối cơ quan/CN Tân Sơn Nhất",
    value: DepartmentCode.TSN,
  },
  {
    label: "CN Nội Bài",
    value: DepartmentCode.NB,
  },
  {
    label: "CN Đà Nẵng",
    value: DepartmentCode.DN,
  },
];

const MonthlyTrainingPlanSummary = withProcedureWraper(() => {
  const history = useHistory();
  const location = useLocation();
  const [form] = Form.useForm();
  const params = new URLSearchParams(location.search);
  const defaultMonth = params.get("month");
  const defaultDepartment = params.get("department");

  const { data, isLoading: isMTPRequestsLoading } = useMTPRequests();

  const {
    data: myRequests,
    requests,
    isLoading: isMTPSMyRequestsLoading,
  } = useMTPSMyRequests();
  const { columns } = useMTPSColumns();

  const handleFilter = async () => {
    try {
      await form.validateFields();
      const { month, department } = form.getFieldsValue(true);
      params.set("month", month.toISOString());
      params.set("department", department);
      const updatedParamsString = params.toString();
      history.replace({
        pathname: window.location.pathname,
        search: updatedParamsString,
      });
    } catch (error) {}
  };

  const createRequest = async () => {
    try {
      await form.validateFields();
      const updatedParamsString = params.toString();
      history.push({
        pathname: Routes.createMTPSPage.path,
        search: updatedParamsString,
      });
    } catch (error) {}
  };

  const dataSource: any[] = useMemo(() => {
    if (!defaultMonth || !defaultDepartment || !data) return [];
    const filteredRequests = data.filter((item: any) => {
      const { eMonth, eRequestStatus } = item;
      const isApprovedRequest = eRequestStatus === RequestStatus.completed;
      const isSameMonth = moment(defaultMonth).isSame(eMonth, "month");
      return isApprovedRequest && isSameMonth;
    });
    return filteredRequests.filter(({ eDepartment }: any) => {
      const depCode = StringHelper.verticalBarSplit(eDepartment)[0];
      if (defaultDepartment === "1" && !["2", "3"].includes(depCode)) {
        return true;
      }
      if (defaultDepartment === depCode) return true;
      return false;
    });
  }, [data, defaultMonth, defaultDepartment]);

  const handleExport = () => {
    if (dataSource.length === 0) return;
    const items = transform(dataSource);
    const instance = new MTPExcelJSService();
    instance.create(items);
    instance.download(`Bảng tổng hợp kế hoạch đào tạo tháng`);
  };

  const isLoading = isMTPRequestsLoading || isMTPSMyRequestsLoading;

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        month: defaultMonth ? moment(defaultMonth) : undefined,
        department: defaultDepartment,
      }}
    >
      <div className="pd15">
        <UserManual title="Bảng tổng hợp kế hoạch đào tạo tháng" />

        <Box title="Danh sách kế hoạch đào tạo tháng">
          <div style={{ marginBottom: "1rem" }}>
            <Row gutter={[32, 0]}>
              <Col span={12}>
                <Form.Item
                  name="month"
                  label="Thời gian khoá học"
                  rules={[{ required: true, message: "Vui lòng chọn tháng!" }]}
                >
                  <DatePicker
                    size="large"
                    format={DateFormat.MM_YYYY}
                    picker="month"
                    placeholder="Chọn tháng..."
                    className="min-w-full"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="department"
                  label="Cơ quan/Đơn vị"
                  rules={[
                    {
                      required: true,
                      message: "Vui lòng chọn cơ quan/đơn vị!",
                    },
                  ]}
                >
                  <Select
                    size="large"
                    options={depOptions}
                    placeholder="Chọn cơ quan/đơn vị..."
                    className="min-w-full"
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <Space style={{ marginBottom: "1rem" }}>
            <Button onClick={handleFilter}>Lọc</Button>
            <Button onClick={handleExport}>Xuất file</Button>
            <Button variant="primary" onClick={createRequest}>
              Tạo yêu cầu
            </Button>
          </Space>
          <MTPDetailTable dataSource={dataSource} />
        </Box>
        <Box title="Yêu cầu của tôi">
          <Table
            rowKey="id"
            loading={isLoading}
            dataSource={myRequests}
            columns={columns}
            onRow={(record: any) => {
              return {
                style: { cursor: "pointer" },
                onClick: () =>
                  history.push(`${Routes.mtpsPage.path}/${record.id}`),
              };
            }}
          />
        </Box>
        <Box title="Yêu cầu cần duyệt">
          <Table
            rowKey="id"
            loading={isLoading}
            dataSource={requests}
            columns={columns}
            onRow={(record: any) => {
              return {
                style: { cursor: "pointer" },
                onClick: () =>
                  history.push(`${Routes.mtpsPage.path}/${record.id}`),
              };
            }}
          />
        </Box>
      </div>
    </Form>
  );
});

export default MonthlyTrainingPlanSummary;
